import { ChevronLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useGoogleFont } from '@/features/customization/hooks/useGoogleFont';
import {
  parseCustomization,
  toBrandCssVars,
} from '@/features/customization/schemas/customizationSchemas';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';

import { useSession } from '../api/useSession';
import { SessionMobilePanel } from '../components/SessionMobilePanel';

/** Fill / Execute page — minimal shell for session start and resume. No sidebar, no nav chrome. */
export function SessionFillScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data: session, isLoading } = useSession(sessionId);

  // Sync tenant/profile so API interceptors have the correct tenant header.
  useCurrentProfile();

  // Apply the resolved branding (org default or per-workflow override) to the
  // whole journey via `--brand-*` CSS variables; screens read var(--brand-*).
  // The logo falls back to SignCare when the tenant hasn't set one. The picked
  // typography is loaded (Google Fonts) and applied to the whole journey.
  const branding = parseCustomization(session?.customization?.customizationJson);
  const brandVars = toBrandCssVars(branding);
  useGoogleFont(session?.customization ? branding.typographyStyle : undefined);

  return (
    <div className="brand-scope relative flex min-h-dvh flex-col bg-[#F1F5FB]" style={brandVars}>
      {/* No header bar — the journey card carries the brand logo (matching the
          Customization preview). Back is a floating button at the top-left. */}
      <Link
        to="/sessions"
        aria-label="Back to sessions"
        className="absolute start-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E7EB] bg-white text-[#4B5563] shadow-sm transition-colors hover:bg-[#F3F4F6] md:start-6 md:top-6"
      >
        <ChevronLeft size={20} />
      </Link>

      {/* Full-height form area */}
      <div className="flex min-h-0 flex-1 flex-col">
        {session ? (
          <SessionMobilePanel session={session} />
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-[#666666]">
            {isLoading ? 'Loading session…' : 'Session not found.'}
          </div>
        )}
      </div>
    </div>
  );
}
